import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Calendar, CreditCard } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { formatCurrency } from "@/utils/currencyUtils";

interface CustomerMembershipsSectionProps {
  customerEmail: string;
}

interface CustomerMembership {
  id: string;
  membership_id: string;
  sessions_remaining: number | null;
  sessions_used: number;
  purchase_date: string;
  expires_at: string | null;
  is_active: boolean;
  membership: {
    id: string;
    name: string;
    description: string | null;
    membership_type: string;
    session_count: number | null;
    price: number;
    currency: string;
  };
}

const CustomerMembershipsSection = ({ customerEmail }: CustomerMembershipsSectionProps) => {
  const { data: memberships, isLoading } = useQuery({
    queryKey: ['customer-memberships', customerEmail],
    queryFn: async () => {
      if (!customerEmail) return [];
      
      // First, get the user ID from the profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', customerEmail)
        .single();

      if (profileError || !profile) {
        console.error('Error fetching customer profile:', profileError);
        return [];
      }

      // Then fetch user memberships with membership details
      const { data: userMemberships, error } = await supabase
        .from('user_memberships')
        .select(`
          *,
          membership:memberships(
            id,
            name,
            description,
            membership_type,
            session_count,
            price,
            currency
          )
        `)
        .eq('user_id', profile.id)
        .order('purchase_date', { ascending: false });

      if (error) {
        console.error('Error fetching customer memberships:', error);
        return [];
      }

      return userMemberships as CustomerMembership[];
    },
    enabled: !!customerEmail,
  });

  const getStatusBadge = (membership: CustomerMembership) => {
    if (!membership.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    // Check if membership has expired
    if (membership.expires_at) {
      const now = new Date();
      const expiresAt = new Date(membership.expires_at);
      if (now > expiresAt) {
        return <Badge variant="destructive">Expired</Badge>;
      }
    }
    
    // Check if sessions are remaining for pack_of_sessions
    if (membership.membership.membership_type === 'pack_of_sessions' && 
        membership.sessions_remaining === 0) {
      return <Badge variant="secondary">Used Up</Badge>;
    }
    
    return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getMembershipTypeDisplay = (type: string) => {
    switch (type) {
      case 'pack_of_sessions':
        return 'Pack of Sessions';
      case 'sessions_per_month':
        return 'Sessions per Month';
      case 'unlimited':
        return 'Unlimited';
      default:
        return type;
    }
  };

  const getCreditsDisplay = (membership: CustomerMembership) => {
    if (membership.membership.membership_type === 'unlimited') {
      return 'Unlimited';
    }
    if (membership.membership.membership_type === 'sessions_per_month') {
      return `${membership.sessions_remaining || 0} this month`;
    }
    return `${membership.sessions_remaining || 0} remaining`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
        <Package className="h-5 w-5" />
        Memberships
      </h3>
      
      {memberships && memberships.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {memberships.map((membership) => (
            <Card key={membership.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base font-medium">
                    {membership.membership.name}
                  </CardTitle>
                  {getStatusBadge(membership)}
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                <div className="text-sm text-gray-600">
                  {getMembershipTypeDisplay(membership.membership.membership_type)}
                </div>
                
                {membership.membership.description && (
                  <div className="text-sm text-gray-600">
                    {membership.membership.description}
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    <span>Credits:</span>
                  </div>
                  <span className="font-medium">{getCreditsDisplay(membership)}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Purchased:</span>
                  </div>
                  <span>{formatDate(membership.purchase_date)}</span>
                </div>
                
                {membership.expires_at && (
                  <div className="flex items-center justify-between text-sm">
                    <span>Expires:</span>
                    <span>{formatDate(membership.expires_at)}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm">
                  <span>Sessions Used:</span>
                  <span>{membership.sessions_used}</span>
                </div>
                
                <div className="text-xs text-gray-500 mt-2">
                  {formatCurrency(membership.membership.price, membership.membership.currency)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
          <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p>No memberships found</p>
          <p className="text-sm mt-1">This customer has not purchased any memberships</p>
        </div>
      )}
    </div>
  );
};

export default CustomerMembershipsSection;
